import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { UserManager, type User } from 'oidc-client-ts'
import { getOidcConfiguration } from '@/services/oidc.service'
import { clearLocalStorage } from '@/utils/localstorage'

let _userManager: UserManager | null = null

function getUserManager(): UserManager {
  if (!_userManager) {
    _userManager = new UserManager(getOidcConfiguration().settings)
  }
  return _userManager
}
// `returnTo` is round-tripped through the OIDC `state` so the Keycloak callback
// (Redirect.vue) can send the user back to the page they were on — e.g. the
// /ibinder-callback route carrying iBinder's ?code=&state= params, which would
// otherwise be lost when we hard-replace to /home after login.
async function signinRedirect(returnTo?: string) {
  await getUserManager().signinRedirect(returnTo ? { state: returnTo } : undefined)
}

async function signoutRedirect(idTokenHint?: string) {
  await getUserManager().signoutRedirect({
    id_token_hint: idTokenHint,
    post_logout_redirect_uri: `${globalThis.location.origin}/realm`
  })
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)

  const accessToken = computed(() => user.value?.access_token ?? undefined)
  const idToken = computed(() => user.value?.id_token ?? undefined)

  async function initialize() {
    const um = getUserManager()

    try {
      user.value = await um.getUser()
    } catch {
      user.value = null
    }

    um.events.addUserLoaded((loadedUser) => {
      user.value = loadedUser
    })
    um.events.addUserUnloaded(() => {
      user.value = null
    })
    um.events.addAccessTokenExpired(() => {
      console.log('Access token expired, signing out')
      clearLocalStorage()
      globalThis.location.replace(globalThis.location.origin + '/oauth2')
    })

    // getUser() loading an existing session does not fire userLoaded, so
    // automaticSilentRenew alone won't schedule renewal — call explicitly.
    um.startSilentRenew()
  }

  async function signinRedirectCallback() {
    user.value = await getUserManager().signinRedirectCallback()
    return user.value
  }

  async function getUser() {
    user.value = await getUserManager().getUser()
    return user.value
  }

  // Silently renews the session (prompt=none via the silent-renew iframe) and
  // refreshes `user` with the latest claims — e.g. after a Keycloak account
  // action (UPDATE_PROFILE / UPDATE_PASSWORD) so the UI reflects the new values.
  async function signinSilent() {
    user.value = await getUserManager().signinSilent()
    return user.value
  }

  return {
    user,
    accessToken,
    idToken,
    initialize,
    signinRedirect,
    signinRedirectCallback,
    signoutRedirect,
    getUser,
    signinSilent
  }
})
